import { Component } from '@angular/core';
import {
  VenueQuoteComparisonRow,
  VenueQuoteModel,
  VenueQuoteLineItem,
  VenueQuoteVariantBreakdown,
} from '../shared/venue-quote.model';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { CurrencyPipe, DatePipe, NgTemplateOutlet } from '@angular/common';
import { LucideAngularModule, Calendar, CheckCircle, Globe, Info, Mail, Phone } from 'lucide-angular';
import { BadgeComponent } from '@app/shared/components/badge/badge.component';
import { TippyDirective } from '@app/shared/directives/tippy.directive';
import { FeatureToggleService } from '@app/feature-toggles/feature-toggle.service';
import { HelpPanelComponent } from '@app/partials/help-panel/help-panel.component';
import { AuthService } from '@app/auth/auth.service';

@Component({
  selector: 'app-quote-view',
  standalone: true,
  imports: [LucideAngularModule, CurrencyPipe, DatePipe, NgTemplateOutlet, BadgeComponent, RouterLink, TippyDirective, HelpPanelComponent],
  templateUrl: './quote-view.component.html',
  providers: [CurrencyPipe, DatePipe],
})
export class QuoteViewComponent {
  protected quote?: VenueQuoteModel;
  protected selectedDate: string | null = null;

  // TVG-173: the per-date "Convert to event" actions are part of the CRM sales
  // flow. When the toggle is off we keep the legacy single "Create event"
  // button so the page stays byte-identical to before.
  protected readonly crmSalesFlowEnabled: boolean;

  /**
   * TVG-352: raising an event is for admins and managers, so standard users —
   * who can still read a quote — do not get a convert action that would bounce
   * off the route guard.
   */
  protected canCreateEvents = false;

  protected readonly MailIcon = Mail;
  protected readonly PhoneIcon = Phone;
  protected readonly GlobeIcon = Globe;
  protected readonly InfoIcon = Info;
  protected readonly CalculatedIcon = Calendar;
  protected readonly SubmittedIcon = CheckCircle;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private currencyPipe: CurrencyPipe,
    private datePipe: DatePipe,
    private featureToggleService: FeatureToggleService,
    private authService: AuthService,
  ) {
    this.quote = this.route.snapshot.data['quote'];
    this.crmSalesFlowEnabled = this.featureToggleService.isEnabled('crm-sales-flow');
    this.authService.getUser().subscribe({
      next: (user) => {
        this.canCreateEvents = (user?.roles ?? []).some(
          (role: string) => role === 'admin' || role === 'manager',
        );
      },
      error: () => {
        this.canCreateEvents = false;
      },
    });
    this.initializeSelectedDate();
  }

  /**
   * TVG-173: the quote's candidate dates, each of which can be converted into
   * its own event. Prefers the priced comparison rows (the date tabs), falling
   * back to the raw selected dates and finally the submitted preferred date.
   */
  protected get convertibleDates(): string[] {
    const fromComparison = this.comparisonRows
      .map((row) => row.date)
      .filter((date): date is string => !!date);
    if (fromComparison.length > 0) {
      return fromComparison;
    }

    const fromSelected = (this.quote?.selected_dates ?? []).filter((date): date is string => !!date);
    if (fromSelected.length > 0) {
      return fromSelected;
    }

    return this.quote?.preferred_date ? [this.quote.preferred_date] : [];
  }

  protected get comparisonRows(): VenueQuoteComparisonRow[] {
    return this.quote?.comparison_rows ?? [];
  }

  protected get lineItems(): VenueQuoteLineItem[] {
    if (this.selectedDate && this.quote?.line_items_by_date?.[this.selectedDate]) {
      return this.quote.line_items_by_date[this.selectedDate];
    }

    return this.quote?.line_items ?? [];
  }

  // TVG-389: the only line item billed against the evening session is evening
  // food — everything else (venue hire, wedding breakfast, drinks) is a day
  // charge. Grouping under "Day" / "Evening" headings mirrors the column
  // naming already shipped on the customer-facing quote list even though this
  // staff view lists items rather than guest counts.
  protected get dayItems(): VenueQuoteLineItem[] {
    return this.lineItems.filter((item) => item.key !== 'evening_food');
  }

  protected get eveningItems(): VenueQuoteLineItem[] {
    return this.lineItems.filter((item) => item.key === 'evening_food');
  }

  protected get hasDateTabs(): boolean {
    return this.comparisonRows.length > 1;
  }

  protected selectDate(date: string): void {
    this.selectedDate = date;
  }

  protected isSelectedDate(date: string): boolean {
    return this.selectedDate === date;
  }

  protected formattedDisplayedDate(): string {
    if (!this.selectedDate) {
      return '—';
    }

    return this.datePipe.transform(this.selectedDate, 'EEEE, d MMMM y') ?? '—';
  }

  protected displayedDateLabel(): string {
    return this.quote?.status === 'draft' ? 'Calculated date (not submitted)' : 'Submitted preferred date';
  }

  protected selectedItemsDescription(): string {
    if (this.quote?.status === 'draft') {
      return 'Breakdown for calculated options. Customer has not submitted a preferred date yet.';
    }

    return 'Breakdown for the submitted preferred date.';
  }

  protected formattedPreferredDate(): string {
    if (!this.quote?.preferred_date) {
      return '—';
    }

    return this.datePipe.transform(this.quote.preferred_date, 'EEEE, d MMMM y') ?? '—';
  }

  protected formattedTotal(): string {
    const selectedTotal = this.selectedComparison()?.total;
    if (selectedTotal !== null && selectedTotal !== undefined) {
      return this.currencyPipe.transform(selectedTotal, 'GBP', 'symbol', '1.0-0') ?? '—';
    }

    if (this.quote?.preferred_total !== null && this.quote?.preferred_total !== undefined) {
      return this.currencyPipe.transform(this.quote.preferred_total, 'GBP', 'symbol', '1.0-0') ?? '—';
    }

    if (this.quote?.estimated_total_label) {
      return this.quote.estimated_total_label;
    }

    return '—';
  }

  protected formattedDateShort(date: string): string {
    return this.datePipe.transform(date, 'd MMM yyyy') ?? date;
  }

  protected tabDateTypeLabel(date: string): string {
    if (this.quote?.status === 'draft') {
      return 'Calculated';
    }

    if (this.quote?.preferred_date && this.quote.preferred_date === date) {
      return 'Submitted';
    }

    return 'Calculated';
  }

  protected tabDateTypeTooltip(date: string): string {
    const type = this.tabDateTypeLabel(date);
    if (type === 'Submitted') {
      return 'Submitted preferred date';
    }

    return 'Calculated option (not submitted)';
  }

  protected tabDateTypeIcon(date: string) {
    return this.tabDateTypeLabel(date) === 'Submitted'
      ? this.SubmittedIcon
      : this.CalculatedIcon;
  }

  protected formatVariantLabel(item: VenueQuoteLineItem): string | null {
    if (!item.option_name) {
      return null;
    }

    const category = item.option_category;
    if (!category || category === 'Full Price') {
      return item.option_name;
    }

    return `${item.option_name} (${category})`;
  }

  protected formatVariantBreakdownLabel(entry: VenueQuoteVariantBreakdown): string {
    const name = entry.option_name || 'Variant';
    if (!entry.option_category) {
      return name;
    }

    return `${name} (${entry.option_category})`;
  }

  protected goToEventCreate(chosenDate?: string): void {
    if (!this.quote) {
      return;
    }

    // TVG-173: seed the one chosen candidate date into Event Date (kept
    // editable on the Add Event screen). Omitting it preserves the legacy
    // behaviour of seeding the preferred / first date.
    const queryParams: Record<string, string | number | null> = {
      quoteId: this.quote.id,
      venueId: this.quote.venue_id,
    };
    if (chosenDate) {
      queryParams['eventDate'] = chosenDate;
    }

    this.router.navigate(['/events/create'], { queryParams });
  }

  protected goToEventView(): void {
    if (!this.quote?.event_id) {
      return;
    }

    this.router.navigate(['/events', this.quote.event_id]);
  }

  private initializeSelectedDate(): void {
    if (!this.quote) {
      this.selectedDate = null;

      return;
    }

    const comparisonDates = this.comparisonRows.map((row) => row.date).filter((date): date is string => !!date);
    if (comparisonDates.length === 0) {
      this.selectedDate = this.quote.preferred_date ?? null;

      return;
    }

    const preferred = this.quote.preferred_date;
    if (preferred && comparisonDates.includes(preferred)) {
      this.selectedDate = preferred;

      return;
    }

    const draftDefault = this.quote.draft_default_date;
    if (draftDefault && comparisonDates.includes(draftDefault)) {
      this.selectedDate = draftDefault;

      return;
    }

    const recommended = this.quote.recommended_date;
    if (recommended && comparisonDates.includes(recommended)) {
      this.selectedDate = recommended;

      return;
    }

    this.selectedDate = comparisonDates[0];
  }

  private selectedComparison(): VenueQuoteComparisonRow | undefined {
    if (!this.selectedDate) {
      return undefined;
    }

    return this.comparisonRows.find((row) => row.date === this.selectedDate);
  }
}
